import * as React from 'react';
import { useMemo, useState } from 'react';
import { useAuthProvider, useLocales, useResourceDefinitions, useThemesContext, } from 'react-admin';
import { styled } from '@mui/material';
import { useAppLocationState } from '../app-location';
import { SolarPrimarySidebar } from './SolarPrimarySidebar';
import { SolarSecondarySidebar } from './SolarSecondarySidebar';
import { SolarMenuItem } from './SolarMenuItem';
import { SolarMenuContextProvider, } from './SolarMenuContext';
import { SolarMenuList } from './SolarMenuList';
import { SolarMenuUserItem } from './SolarMenuUserItem';
import { SolarMenuToggleThemeItem } from './SolarMenuToggleThemeItem';
import { SolarMenuUserProfileItem } from './SolarMenuUserProfileItem';
import { SolarMenuLocalesItem } from './SolarMenuLocalesItem';
import { SolarMenuLoadingIndicatorItem } from './SolarMenuLoadingIndicatorItem';
import { SolarMenuResourceItem } from './SolarMenuResourceItem';
import { SolarMenuDashboardItem } from './SolarMenuDashboardItem';
/**
 * A menu that displays as a vertical sidebar and can show more content in a secondary sidebar inside a drawer.
 * Used in the SolarLayout.
 * By defaults, it will render a list of menu items for the dashboard and all registered resources without any secondary content for the drawer.
 * You can customize it by passing children, leveraging the related components: SolarMenuList, SolarMenuItem and SolarMenuUserItem.
 * You can also override the primary sidebar and the secondary sidebar by passing them as props.
 *
 * @example <caption>Custom menu</caption>
 * import { Admin, Resource } from 'react-admin';
 * import { SolarLayout, SolarMenu, SolarMenuProps } from '@react-admin/ra-navigation';
 * import { ListSubheader } from '@mui/material';
 * import { Logo } from './Logo';
 *
 * const CustomMenu = () => (
 *     <SolarMenu>
 *         <Logo sx={{ marginBottom: theme => theme.spacing(2) }} />
 *         <SolarMenu.List>
 *             <SolarMenu.DashboardItem />
 *             <SolarMenu.ResourceItem name="songs">
 *                 <SolarMenu.List disablePadding>
 *                     <SolarMenu.Item
 *                         name="songs.all"
 *                         primaryText="All Songs"
 *                         to={`/songs?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: {} })
 *                         )}`}
 *                     />
 *                     <ListSubheader sx={{ marginTop: 2 }}>Rock</ListSubheader>
 *                     <SolarMenu.Item
 *                         name="songs.rock"
 *                         primaryText="Rock Songs"
 *                         to={`/songs?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Rock' } })
 *                         )}`}
 *                     />
 *                     <SolarMenu.Item
 *                         name="songs.folk"
 *                         primaryText="Folk Rock Songs"
 *                         to={`/songs?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Folk Rock' } })
 *                         )}`}
 *                     />
 *                     <SolarMenu.Item
 *                         name="songs.rock"
 *                         primaryText="Pop Rock Songs"
 *                         to={`/songs?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Pop Rock' } })
 *                         )}`}
 *                     />
 *                     <ListSubheader sx={{ marginTop: 2 }}>Jazz</ListSubheader>
 *                     <SolarMenu.Item
 *                         name="songs.jazz"
 *                         primaryText="Jazz songs"
 *                         to={`/songs?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Jazz' } })
 *                         )}`}
 *                     />
 *                     <SolarMenu.Item
 *                         name="songs.rb"
 *                         primaryText="RB Songs"
 *                         to={`/songs?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'RB' } })
 *                         )}`}
 *                     />
 *                 </SolarMenu.List>
 *             </SolarMenu.ResourceItem>
 *             <SolarMenu.ResourceItem name="artists">
 *                 <SolarMenu.List disablePadding>
 *                     <SolarMenu.Item
 *                         name="artists.all"
 *                         primaryText="All Artists"
 *                         to={`/artists?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: {} })
 *                         )}`}
 *                     />
 *                     <ListSubheader sx={{ marginTop: 2 }}>Rock</ListSubheader>
 *                     <SolarMenu.Item
 *                         name="artists.folk"
 *                         primaryText="Folk Rock Artists"
 *                         to={`/artists?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Folk Rock' } })
 *                         )}`}
 *                     />
 *                     <SolarMenu.Item
 *                         name="artists.rock"
 *                         primaryText="Pop Rock Artists"
 *                         to={`/artists?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Pop Rock' } })
 *                         )}`}
 *                     />
 *                     <ListSubheader sx={{ marginTop: 2 }}>Jazz</ListSubheader>
 *                     <SolarMenu.Item
 *                         name="artists.jazz"
 *                         primaryText="Jazz artists"
 *                         to={`/artists?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'Jazz' } })
 *                         )}`}
 *                     />
 *                     <SolarMenu.Item
 *                         name="artists.jazz"
 *                         primaryText="RB Artists"
 *                         to={`/artists?filter=${encodeURIComponent(
 *                             JSON.stringify({ filter: { type: 'RB' } })
 *                         )}`}
 *                     />
 *                 </SolarMenu.List>
 *             </SolarMenu.ResourceItem>
 *         </SolarMenu.List>
 *     </SolarMenu>
 * );
 */
export var SolarMenu = function (_a) {
    var _b = _a.bottomToolbar, bottomToolbar = _b === void 0 ? null : _b, children = _a.children, hasDashboard = _a.hasDashboard, _c = _a.logo, logo = _c === void 0 ? null : _c, _d = _a.userMenu, userMenu = _d === void 0 ? null : _d;
    var definitions = useResourceDefinitions();
    var authProvider = useAuthProvider();
    var darkTheme = useThemesContext().darkTheme;
    var locales = useLocales();
    var _e = useState(null), secondaryContent = _e[0], setSecondaryContent = _e[1];
    var primaryContext = useMemo(function () { return ({
        renderSlot: 'primary',
        setSecondaryContent: setSecondaryContent,
    }); }, []);
    var secondaryContext = useMemo(function () { return ({
        renderSlot: 'secondary',
        setSecondaryContent: setSecondaryContent,
    }); }, []);
    var location = useAppLocationState()[0];
    if (!location.path)
        return null;
    var showUserMenu = authProvider != null || darkTheme || (locales && locales.length > 1);
    return (React.createElement(Root, null,
        React.createElement(SolarPrimarySidebar, null,
            React.createElement(SolarMenuContextProvider, { value: primaryContext },
                children ? (children) : (React.createElement(React.Fragment, null,
                    React.createElement(SolarMenuList, { className: SolarMenuClasses.topToolbar },
                        hasDashboard && (React.createElement(SolarMenuDashboardItem, { icon: logo })),
                        Object.keys(definitions).map(function (name) { return (React.createElement(SolarMenuResourceItem, { key: name, name: name })); })))),
                bottomToolbar ? (bottomToolbar) : (React.createElement(SolarMenuList, { className: SolarMenuClasses.bottomToolbar },
                    React.createElement(SolarMenuLoadingIndicatorItem, null),
                    userMenu ? (userMenu) : showUserMenu ? (React.createElement(SolarMenuUserItem, null)) : null)))),
        React.createElement(SolarSecondarySidebar, null,
            React.createElement(SolarMenuContextProvider, { value: secondaryContext },
                React.createElement(React.Fragment, null, secondaryContent)))));
};
var PREFIX = 'RaSolarMenu';
export var SolarMenuClasses = {
    root: "".concat(PREFIX, "-root"),
    topToolbar: "".concat(PREFIX, "-topToolbar"),
    bottomToolbar: "".concat(PREFIX, "-bottomToolbar"),
};
var Root = styled('div', {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function () {
    var _a;
    return (_a = {},
        _a["& .".concat(SolarMenuClasses.bottomToolbar)] = {
            marginTop: 'auto',
        },
        _a);
});
SolarMenu.Item = SolarMenuItem;
SolarMenu.DashboardItem = SolarMenuDashboardItem;
SolarMenu.ResourceItem = SolarMenuResourceItem;
SolarMenu.List = SolarMenuList;
SolarMenu.UserItem = SolarMenuUserItem;
SolarMenu.ToggleThemeItem = SolarMenuToggleThemeItem;
SolarMenu.UserProfileItem = SolarMenuUserProfileItem;
SolarMenu.LocalesItem = SolarMenuLocalesItem;
SolarMenu.LoadingIndicatorItem = SolarMenuLoadingIndicatorItem;
